"use client";

import { Suspense, useMemo, useSyncExternalStore } from "react";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { useLocaleStore } from "@/store/locale.store";
import { PersonaRing } from "@/components/assistant/persona-ring";

const APP_STORE_URL = "https://apps.apple.com/dz/app/fast-livraison-repas-courses/id1469020170";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.customer.fastdelivery";
const ANDROID_PACKAGE = "com.customer.fastdelivery";
/** Fond de la page (vert Fast sombre) : aussi le « papier » derrière les avatars. */
const PAPER = "#03331a";

const COPY = {
  fr: {
    kicker: "Dans l'app Fast",
    title: "Dites ce dont vous avez envie.",
    subtitle: "L'assistant Fast trouve la boutique, compose votre panier et vous amène au paiement. Vous parlez, il commande.",
    meet: "Choisissez qui vous parle",
    meetSub: "Sept personnages, un même assistant. Chacun a sa voix, sa tête, ce qu'il aime proposer. Faites tourner la ronde.",
    ring: { hint: "Glissez pour faire tourner, cliquez pour faire venir", character: "Caractère", voice: "Sa voix", fits: "Pour qui" },
    more: "Ce qu'il sait faire",
    features: [
      { title: "Trouver", body: "« Une pizza pour deux, livrée. » Il cherche parmi les boutiques ouvertes près de vous, compare et propose." },
      { title: "Composer", body: "Il ajoute au panier, ajuste les quantités, applique les codes promo et calcule le total exact." },
      { title: "Suivre", body: "« Où en est ma commande ? » Il suit votre livraison en direct, avec le livreur sur la carte." },
      { title: "Se souvenir", body: "Vos allergies, votre régime, vos habitudes : il les garde d'une fois sur l'autre. Vous pouvez lui dire d'oublier." },
    ],
    open: "Ouvrir dans l'app",
    get: "Pas encore l'app ?",
    appStore: "Télécharger dans l'App Store",
    playStore: "Disponible sur Google Play",
    note: "Les prix affichés par l'assistant viennent toujours des boutiques, jamais du modèle. Le paiement reste dans l'app.",
  },
  en: {
    kicker: "In the Fast app",
    title: "Say what you feel like.",
    subtitle: "The Fast assistant finds the store, builds your cart and takes you to checkout. You talk, it orders.",
    meet: "Pick who talks to you",
    meetSub: "Seven characters, one assistant. Each has a voice, a face, and things it loves to suggest. Spin the ring.",
    ring: { hint: "Drag to turn, click to bring forward", character: "Character", voice: "Their voice", fits: "For whom" },
    more: "What it can do",
    features: [
      { title: "Find", body: "“A pizza for two, delivered.” It searches the stores open near you, compares and suggests." },
      { title: "Build", body: "It adds to the cart, adjusts quantities, applies promo codes and computes the exact total." },
      { title: "Track", body: "“Where is my order?” It follows your delivery live, with the rider on the map." },
      { title: "Remember", body: "Your allergies, your diet, your habits: it keeps them from one time to the next. You can tell it to forget." },
    ],
    open: "Open in the app",
    get: "Don't have the app yet?",
    appStore: "Download on the App Store",
    playStore: "Get it on Google Play",
    note: "Prices shown by the assistant always come from the stores, never from the model. Payment stays in the app.",
  },
} as const;

const subscribeNoop = () => () => {};
const readIsAndroid = () => /android/i.test(navigator.userAgent);

const OPEN_BUTTON_CLASS =
  "inline-flex h-14 w-full items-center justify-center rounded-full bg-primary px-8 text-lg font-bold text-secondary transition-opacity hover:opacity-90 sm:w-auto";

/**
 * Le seul morceau qui dépend de `?q=`, isolé derrière sa propre limite Suspense.
 *
 * `useSearchParams` diffère au client TOUT ce qui se trouve sous sa limite : la
 * page entière était enveloppée dans `<Suspense fallback={null}>`, donc un
 * crawler recevait un corps vide sur une URL indexable et soumise au sitemap.
 * Seul le bouton a besoin de la query ; le reste de la page est statique.
 */
function OpenInAppButton({ label }: { label: string }) {
  const params = useSearchParams();
  const q = params.get("q")?.trim() ?? "";

  // Lien vers l'app : scheme direct sur iOS ; sur Android une URL `intent:`
  // ouvre l'app si elle est là et retombe sur le Play Store sinon.
  const { iosHref, androidHref } = useMemo(() => {
    const query = q ? `?q=${encodeURIComponent(q)}` : "";
    return {
      iosHref: `fast://assistant${query}`,
      androidHref: `intent://assistant${query}#Intent;scheme=fast;package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`,
    };
  }, [q]);
  // Plateforme lue côté client seulement (rendu serveur = iOS), sans
  // décalage d'hydratation : React rejoue le rendu avec la vraie valeur.
  const isAndroid = useSyncExternalStore(subscribeNoop, readIsAndroid, () => false);

  return (
    <a href={isAndroid ? androidHref : iosHref} className={OPEN_BUTTON_CLASS}>
      {label}
    </a>
  );
}

export function AssistantLanding() {
  const locale = useLocaleStore((s) => s.locale);
  const lang = locale === "fr" ? "fr" : "en";
  const t = COPY[lang];

  const appStoreBadge = lang === "fr" ? "/Download_on_the_App_Store_Badge_FRCA_RGB_blk.png" : "/Download_on_the_App_Store_Badge.png";
  const playBadge = lang === "fr" ? "/googleplayFR.png" : "/Google_Play_Store_badge_EN.png";

  return (
    <main className="min-h-screen text-white" style={{ backgroundColor: PAPER }}>
      <div className="mx-auto flex max-w-4xl flex-col px-6 py-8 sm:px-10">
        <header className="flex items-center justify-between">
          <Link href="/" aria-label="Fast">
            <Image src="/fast-logo.svg" alt="Fast" width={100} height={40} className="h-8 w-auto brightness-0 invert" priority />
          </Link>
        </header>

        {/* Accroche */}
        <section className="flex flex-col gap-5 py-12 sm:py-16">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary">{t.kicker}</p>
          <h1 className="text-4xl font-bold leading-tight sm:text-6xl">{t.title}</h1>
          <p className="max-w-2xl text-lg text-white/80 sm:text-xl">{t.subtitle}</p>
        </section>

        {/* La ronde des personas */}
        <section className="flex flex-col items-center gap-6 py-6 sm:py-10">
          <div className="flex flex-col items-center gap-2 text-center">
            <h2 className="text-2xl font-bold sm:text-3xl">{t.meet}</h2>
            <p className="max-w-xl text-white/75">{t.meetSub}</p>
          </div>
          <PersonaRing lang={lang} dark paper={PAPER} labels={t.ring} />
        </section>

        {/* En savoir plus */}
        <section className="flex flex-col gap-6 py-10 sm:py-14">
          <h2 className="text-2xl font-bold sm:text-3xl">{t.more}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {t.features.map((f) => (
              <div key={f.title} className="rounded-3xl bg-white/10 p-5">
                <p className="text-lg font-bold text-primary">{f.title}</p>
                <p className="mt-1 text-white/85">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Stores */}
        <section className="flex flex-col gap-4 py-6">
          <Suspense
            fallback={
              // Rendu statique et pendant l'hydratation : le lien marche déjà,
              // sans le `?q=` que seul le client peut lire.
              <a href="fast://assistant" className={OPEN_BUTTON_CLASS}>
                {t.open}
              </a>
            }
          >
            <OpenInAppButton label={t.open} />
          </Suspense>
          <p className="text-sm text-white/70">{t.get}</p>
          <div className="flex flex-wrap items-center gap-3">
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label={t.appStore} className="transition-opacity hover:opacity-80">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={appStoreBadge} alt={t.appStore} className="block h-[50px] w-[160px]" />
            </a>
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label={t.playStore} className="transition-opacity hover:opacity-80">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={playBadge} alt={t.playStore} className="block h-[50px] w-[160px]" />
            </a>
          </div>
        </section>

        <footer className="py-6 text-xs text-white/60">{t.note}</footer>
      </div>
    </main>
  );
}
